import type { CurrentUserStatus } from 'types/didomi'
import type { CmpAdapter, CmpDeps, ConsentMode } from './types'
import { on } from 'shared/javascripts/util/events'

// Maps the vendor names used throughout the app (also passed from Ruby via `data-vendor`) to the
// vendor ids returned by getCurrentUserStatus(). Didomi has no name-based lookup, so we resolve names
// here. These are the bare ids without the `c:` custom-vendor namespace prefix used elsewhere in the
// Didomi configuration, because getCurrentUserStatus() keys its vendors without that prefix.
const VENDOR_IDS: Record<string, string> = {
  Amplitude: 'amplitude-7jkepPgf',
  'Google Advertising Products': 'google',
  'Google Analytics': 'googleana-4TXnJigR',
  Convert: 'convert-CAd27UM8',
  YouTube: 'youtube',
  'Meta (Formerly Facebook)': 'metanetwo-MCRqeNnG',
  'Meta Pixel': 'metapixel-7VzT98VA',
  'TikTok Ad Network': 'tiktokad-9TKVDC6d',
  'Vimeo.com, Inc.': 'vimeo',
}

const VENDOR_LINK_SELECTOR = 'a[href*="preferences.show(\'vendors\')"]'

// The Didomi adapter also backs the test/disabled modes ('stub', 'grant_all', 'disabled'): those use a
// stand-in Didomi instance (see didomi_fake.ts) that drives the exact same readiness/event wiring.
export function createDidomiAdapter(
  deps: CmpDeps,
  mode: Exclude<ConsentMode, 'sourcepoint'>,
): CmpAdapter {
  // Cache of getCurrentUserStatus(); populated lazily and invalidated on every consent change so the
  // next read is fresh.
  let cachedUserStatus: CurrentUserStatus | null = null

  function getUserStatus(): CurrentUserStatus | null {
    if (!cachedUserStatus) {
      try {
        cachedUserStatus = window.Didomi?.getCurrentUserStatus() ?? null
      } catch {
        console.error('[Didomi] Could not read user consent status')
      }
    }
    return cachedUserStatus
  }

  function notifyConsentChanged(): void {
    cachedUserStatus = null
    deps.signalConsentChanged()
  }

  return {
    init() {
      window.didomiOnReady = window.didomiOnReady || []
      window.didomiOnReady.push((didomi) => {
        // When no banner needs to be shown (consent already stored, or not required), consent is ready
        // right away. Otherwise we wait for the `consent.changed` event fired after the user decides.
        if (!didomi.shouldUserStatusBeCollected()) {
          notifyConsentChanged()
        }
      })

      window.didomiEventListeners = window.didomiEventListeners || []
      window.didomiEventListeners.push(
        { event: 'notice.shown', listener: () => deps.markBannerShown() },
        { event: 'consent.changed', listener: () => notifyConsentChanged() },
      )

      // The banner's "x Partner" link is a `javascript:` href, which our CSP blocks, so the link does
      // nothing. Didomi renders every other link correctly; they consider this our CSP's problem, so
      // we open the vendor list ourselves instead of weakening script-src with 'unsafe-hashes'.
      on(document, 'click', (event: MouseEvent) => {
        const target = event.target instanceof Element ? event.target : null
        if (!target?.closest(VENDOR_LINK_SELECTOR)) return

        event.preventDefault()
        window.Didomi?.preferences.show('vendors')
      })

      if (mode === 'didomi') {
        // The SDK loader is defined inline (see _gdpr_consent.html.erb) and, on non-delay pages, has
        // already started loading before this bundle ran. We (re)trigger it once messaging is allowed;
        // it is idempotent, so this is a no-op when it already fired and the actual trigger on delay
        // pages.
        deps.whenMessagingAllowed(() => {
          try {
            window.loadDidomi?.()
          } catch {
            console.error('[Didomi] Could not load the consent SDK')
            notifyConsentChanged()
          }
        })
      } else {
        // Tests and disabled contexts use a stand-in Didomi instance instead of loading the real SDK.
        void import('./didomi_fake').then(({ installFakeDidomi }) => {
          deps.whenMessagingAllowed(() => installFakeDidomi(mode))
        })
      }
    },

    // hasVendorGrant is true when the user consents to the vendor and everything the vendor needs:
    // Didomi's getCurrentUserStatus() already folds the vendor's required purposes into `enabled`.
    hasVendorGrant(vendorName: string): boolean {
      const vendorId = VENDOR_IDS[vendorName]
      if (!vendorId) {
        console.error(
          `[Didomi] hasVendorGrant() called with unknown vendor name "${vendorName}" — add it to VENDOR_IDS.`,
        )
        return false
      }

      const vendors = getUserStatus()?.vendors
      // Guard against drifting (custom) vendor ids: if the real SDK reports vendors but not this id, the
      // id configured in VENDOR_IDS has likely changed. We only check against the real Didomi SDK — the
      // disabled instance returns an empty vendor set and the test instances use a catch-all proxy.
      if (mode === 'didomi' && vendors && !(vendorId in vendors)) {
        console.error(
          `[Didomi] vendor "${vendorName}" (id "${vendorId}") is missing from getCurrentUserStatus() — the Didomi vendor id may have changed.`,
        )
      }

      return !!vendors?.[vendorId]?.enabled
    },

    openGdprManager(): void {
      if (window.Didomi) {
        window.Didomi.preferences.show()
        return
      }

      // On the delay pages (terms/imprint/privacy) the SDK has not been loaded yet, while the privacy
      // policy points users at this button — so load it on demand and open the preferences once ready.
      if (mode === 'didomi') {
        window.didomiOnReady = window.didomiOnReady || []
        window.didomiOnReady.push((didomi) => didomi.preferences.show())
        window.loadDidomi?.()
      }
    },
  }
}
